import clsx from 'clsx'

import { User } from '../../models/User'
import Avatar from '../app/design/Avatar'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import Tooltip from '../app/design/Tooltip'
import { ContactAvatar } from '../contacts/ContactAvatar'

type Props = {
    pros: User[]
    showLabel?: boolean
    size: 'xs' | 'sm'
}

function ProAvatar({
    user,
    size,
    showTooltip,
}: {
    user: User
    size: 'xs' | 'sm'
    showTooltip: boolean
}) {
    const sizeClasses = {
        xs: 'size-6 text-label-xs',
        sm: 'size-9 text-label-md',
    }

    const avatarBody = user.image?.uri ? (
        <Avatar size={size}>
            <img src={user.image?.uri} className={clsx('rounded-full', sizeClasses[size])} />
        </Avatar>
    ) : (
        <ContactAvatar
            user={user}
            size={size}
            showName={false}
            truncateName={true}
            shouldRestrictToOneLetter={true}
        />
    )

    if (showTooltip) {
        return (
            <Tooltip key={user.id} label={user.name ?? ''} position="top">
                {avatarBody}
            </Tooltip>
        )
    }

    return avatarBody
}

export function ProListTableCell({ pros, size, showLabel = true }: Props) {
    if (!pros.length) return <Paragraph size="sm">-</Paragraph>

    if (pros.length === 1) {
        const p = pros[0]
        return (
            <div className="flex items-center gap-3">
                <ProAvatar user={p} size={size} showTooltip={showLabel} />
                {showLabel && (
                    <Paragraph size="sm" className="whitespace-nowrap">
                        {p.name?.split(' ')[0]}
                    </Paragraph>
                )}
            </div>
        )
    }

    if (pros.length <= 3) {
        return (
            <div className="flex items-center gap-3">
                {pros.map((p) => {
                    return <ProAvatar key={p.id} user={p} size={size} showTooltip={showLabel} />
                })}
                {showLabel && (
                    <Paragraph size="sm" className="whitespace-nowrap">
                        {`${pros.length} pros`}
                    </Paragraph>
                )}
            </div>
        )
    }

    return (
        <div className="flex items-center gap-3">
            {pros.slice(0, 2).map((p) => {
                return <ProAvatar key={p.id} user={p} size={size} showTooltip={showLabel} />
            })}
            <Tooltip
                label={pros.slice(2).map((p) => (
                    <Paragraph key={p.id} size="sm">
                        {p.name}
                    </Paragraph>
                ))}
                position="right"
            >
                <Avatar size={size}>
                    <Label size="xs">{`+${pros.length - 2}`}</Label>
                </Avatar>
            </Tooltip>
            {showLabel && (
                <Paragraph size="sm" className="whitespace-nowrap">
                    {`${pros.length} pros`}
                </Paragraph>
            )}
        </div>
    )
}
